import React from 'react';
import { ChevronLeft, Map as MapIcon, Wind, ChevronRight } from 'lucide-react';
import type { LocationDef } from '#entities/location/index.js';
import { Location } from '@stalker/core';

interface LocationPageProps {
  location: LocationDef;
  rooms: LocationDef[];
  onEnterRoom: (room: Location) => void;
  onBack: () => void;
}

export const LocationPage: React.FC<LocationPageProps> = ({
  location,
  rooms,
  onEnterRoom,
  onBack,
}) => {
  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="bg-card border-y sm:border border-border rounded-none sm:rounded-sm mb-6 overflow-hidden terminal-card w-[calc(100%+2rem)] -mx-4 sm:mx-0 relative">
        <button
          onClick={onBack}
          className="absolute top-3 left-7 md:top-4 md:left-4 group flex items-center gap-2 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] text-primary bg-background/80 px-3 py-1.5 backdrop-blur-sm border border-border/50 hover:bg-primary hover:text-primary-foreground transition-all shadow-xl z-20"
        >
          <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Назад
        </button>

        <div className="w-full aspect-[16/7] md:aspect-[21/9] relative bg-input overflow-hidden">
          {location.imageUrl ? (
            <img
              src={location.imageUrl}
              className="w-full h-full object-cover origin-center sepia-[0.2] brightness-75 transition-all duration-700"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center opacity-10">
              <MapIcon size={80} className="md:size-[120px]" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent opacity-90" />
        </div>

        <div className="p-4 md:p-8 -mt-6 md:-mt-12 relative z-10">
          <h2 className="text-[8px] md:text-[10px] font-black uppercase tracking-[0.4em] text-accent mb-1 md:mb-2 toxic-glow">Объект идентифицирован</h2>
          <h1 className="text-2xl md:text-5xl font-black uppercase tracking-tighter text-foreground mb-2 md:mb-4">{location.name}</h1>
          <p className="text-muted-foreground text-[11px] md:text-base max-w-2xl mb-4 md:mb-8 leading-relaxed">
            {location.description}
          </p>

          <div className="flex flex-wrap gap-2 md:gap-3 mb-6 md:mb-12">
            <div className="px-3 md:px-4 py-1 rounded-sm bg-accent/10 border border-accent/20 text-accent text-[8px] md:text-[10px] font-black uppercase tracking-widest toxic-glow">
              Безопасно
            </div>
            <div className="px-3 md:px-4 py-1 rounded-sm bg-input border border-border text-muted-foreground text-[8px] md:text-[10px] font-black uppercase tracking-widest">
              {rooms.length} секторов
            </div>
            <div className="px-3 md:px-4 py-1 rounded-sm bg-primary/10 border border-primary/20 text-primary text-[8px] md:text-[10px] font-black uppercase tracking-widest">
              Lvl 0{location.dangerLevel}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-1 gap-2 md:gap-4">
            {rooms.map(room => (
              <div
                key={room.id}
                onClick={() => onEnterRoom(room.core)}
                className="group p-3 md:p-6 rounded-sm border border-border bg-input/20 hover:border-accent/30 hover:bg-accent/[0.02] transition-all duration-300 cursor-pointer flex justify-between items-center"
              >
                <div className="flex items-center gap-2 md:gap-4 min-w-0">
                  <div className="w-8 h-8 md:w-12 md:h-12 rounded-sm bg-input border border-border flex items-center justify-center group-hover:bg-accent/10 group-hover:border-accent/20 transition-colors shrink-0">
                    <Wind size={14} className="md:size-20 text-muted-foreground group-hover:text-accent transition-colors" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[9px] md:text-[10px] font-black text-muted-foreground/50 uppercase tracking-widest mb-0.5 md:mb-1 truncate">Сектор</div>
                    <div className="text-xs md:text-sm font-bold uppercase text-foreground group-hover:text-accent transition-colors truncate">
                      {room.name}
                    </div>
                  </div>
                </div>
                <ChevronRight size={12} className="md:size-20 text-muted-foreground/30 group-hover:text-accent group-hover:translate-x-1 transition-all shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
